// frontend/src/components/client/ClientCalendar.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock, Plane, Wrench, Fuel, Users, CheckCircle, XCircle, X } from 'lucide-react';
import { getMyEvents } from '../../services/clientApi';

const EVENT_ICONS = {
  flight: { icon: Plane, color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  fuel: { icon: Fuel, color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
  maintenance: { icon: Wrench, color: 'text-red-400', bgColor: 'bg-red-500/20' },
  meeting: { icon: Users, color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
  appointment: { icon: Calendar, color: 'text-green-400', bgColor: 'bg-green-500/20' }
};

const StatusBadge = ({ status }) => {
  const config = {
    scheduled: { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-400' },
    completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400' },
    cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400' },
    in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400' }
  };
  const { label, color } = config[status] || config.scheduled;
  return <span className={`px-2 py-0.5 rounded-full text-xs ${color}`}>{label}</span>;
};

const ClientCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(() => window.innerWidth < 640 ? 'day' : 'month');
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => { loadEvents(); }, [currentDate, viewMode]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const startDate = getStartDate();
      const endDate = getEndDate();
      const response = await getMyEvents(startDate, endDate);
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Formate une date en YYYY-MM-DD en heure LOCALE (évite le décalage UTC)
  const toLocalDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getStartDate = () => {
    const date = new Date(currentDate);
    if (viewMode === 'month') { date.setDate(1); }
    else if (viewMode === 'week') { date.setDate(date.getDate() - date.getDay()); }
    return toLocalDateStr(date);
  };

  const getEndDate = () => {
    const date = new Date(currentDate);
    if (viewMode === 'month') { date.setMonth(date.getMonth() + 1); date.setDate(0); }
    else if (viewMode === 'week') { date.setDate(date.getDate() + (6 - date.getDay())); }
    else { date.setDate(date.getDate() + 1); } // Day: end = lendemain pour inclure toute la journée
    return toLocalDateStr(date);
  };

  const handlePrev = () => {
    const d = new Date(currentDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() - 1);
    else if (viewMode === 'week') d.setDate(d.getDate() - 7);
    else d.setDate(d.getDate() - 1);
    setCurrentDate(d); setSelectedEvent(null);
  };

  const handleNext = () => {
    const d = new Date(currentDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() + 1);
    else if (viewMode === 'week') d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    setCurrentDate(d); setSelectedEvent(null);
  };

  const formatHeader = () => {
    if (viewMode === 'month') return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (viewMode === 'week') {
      const s = new Date(currentDate); s.setDate(s.getDate() - s.getDay());
      const e = new Date(s); e.setDate(s.getDate() + 6);
      return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const getEventsForDay = (day) => {
    const y = day.getFullYear();
    const m = day.getMonth();
    const d = day.getDate();
    return events.filter(e => {
      // Gère tous les formats : "2026-05-22T18:00:00Z", "2026-05-22T12:00:00", "2026-05-22"
      const raw = e.start_time || "";
      let evDate;
      if (raw.endsWith('Z') || raw.includes('+')) {
        // Timestamp UTC → convertir en local
        evDate = new Date(raw);
      } else if (raw.includes('T')) {
        // Datetime sans timezone → traiter comme local
        evDate = new Date(raw);
      } else {
        // Date seule "2026-05-22" → parser manuellement pour éviter UTC shift
        const [ey, em, ed] = raw.split('-').map(Number);
        return ey === y && (em - 1) === m && ed === d;
      }
      return evDate.getFullYear() === y && evDate.getMonth() === m && evDate.getDate() === d;
    });
  };

  const renderMonthView = () => {
    const year = currentDate.getFullYear(), month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    const weeks = [];
    let current = new Date(startDate);
    while (current <= lastDay) {
      const week = [];
      for (let i = 0; i < 7; i++) { week.push(new Date(current)); current.setDate(current.getDate() + 1); }
      weeks.push(week);
    }
    return (
      <div>
        <div className="grid grid-cols-7 mb-1">
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} className="text-center text-xs text-gray-500 py-1 font-medium">{d}</div>
          ))}
        </div>
        {weeks.map((week, idx) => (
          <div key={idx} className="grid grid-cols-7 gap-px mb-px">
            {week.map((day, di) => {
              const isCurrentMonth = day.getMonth() === month;
              const dayEvents = getEventsForDay(day);
              const isToday = day.toDateString() === new Date().toDateString();
              return (
                <div key={di} onClick={() => { setViewMode('day'); setCurrentDate(new Date(day)); }}
                  className={`min-h-[48px] sm:min-h-[80px] p-0.5 rounded cursor-pointer transition active:opacity-70
                    ${isCurrentMonth ? 'bg-gray-800/50 hover:bg-gray-700/50' : 'bg-gray-900/20'}
                    ${isToday ? 'ring-2 ring-blue-500' : 'border border-gray-700/40'}`}>
                  <div className={`text-right text-xs p-0.5 font-medium
                    ${isToday ? 'text-blue-400' : isCurrentMonth ? 'text-gray-300' : 'text-gray-600'}`}>
                    {day.getDate()}
                  </div>
                  {dayEvents.slice(0, 1).map(ev => {
                    const cfg = EVENT_ICONS[ev.event_type] || EVENT_ICONS.appointment;
                    return (
                      <div key={ev.id} className={`rounded px-0.5 py-px ${cfg.bgColor}`}>
                        <span className={`block truncate leading-tight ${cfg.color}`} style={{ fontSize: '9px' }}>
                          {ev.title}
                        </span>
                      </div>
                    );
                  })}
                  {dayEvents.length > 1 && (
                    <div className="text-gray-500 text-center" style={{ fontSize: '9px' }}>+{dayEvents.length - 1}</div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <p className="text-xs text-gray-500 text-center mt-3">Tap a day to see details</p>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = new Date(currentDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
    return (
      <div className="space-y-2">
        {days.map((day, di) => {
          const dayEvents = getEventsForDay(day);
          const isToday = day.toDateString() === new Date().toDateString();
          return (
            <div key={di} className={`rounded-xl border p-3 ${isToday ? 'border-blue-500 bg-blue-500/5' : 'border-gray-700 bg-gray-800/30'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-sm font-semibold ${isToday ? 'text-blue-400' : 'text-gray-300'}`}>
                  {day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
                {dayEvents.length > 0 && (
                  <span className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded-full">{dayEvents.length}</span>
                )}
              </div>
              {dayEvents.length === 0 ? (
                <p className="text-xs text-gray-600">No events</p>
              ) : (
                <div className="space-y-1">
                  {dayEvents.map(ev => {
                    const cfg = EVENT_ICONS[ev.event_type] || EVENT_ICONS.appointment;
                    const Icon = cfg.icon;
                    return (
                      <div key={ev.id} onClick={() => setSelectedEvent(ev)}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer active:opacity-70 ${cfg.bgColor}`}>
                        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${cfg.color}`} />
                        <span className="text-white text-xs truncate flex-1">{ev.title}</span>
                        <span className="text-gray-400 text-xs flex-shrink-0">{formatTime(ev.start_time)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = getEventsForDay(currentDate).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    return (
      <div className="space-y-3">
        {dayEvents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No events scheduled for this day</p>
          </div>
        ) : (
          dayEvents.map(ev => {
            const cfg = EVENT_ICONS[ev.event_type] || EVENT_ICONS.appointment;
            const Icon = cfg.icon;
            return (
              <div key={ev.id} onClick={() => setSelectedEvent(ev)}
                className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 cursor-pointer hover:bg-gray-800/70 active:bg-gray-700/70 transition">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${cfg.bgColor}`}>
                    <Icon className={`w-5 h-5 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold truncate">{ev.title}</h3>
                      <StatusBadge status={ev.status} />
                    </div>
                    <p className="text-sm text-gray-400 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                      {formatTime(ev.start_time)} – {formatTime(ev.end_time)}
                    </p>
                    {ev.tail_number && <p className="text-xs text-gray-500 mt-1">{ev.tail_number}</p>}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const EventDetailModal = ({ event, onClose }) => {
    if (!event) return null;
    const cfg = EVENT_ICONS[event.event_type] || EVENT_ICONS.appointment;
    const Icon = cfg.icon;
    return (
      <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50">
        <div className="bg-gray-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-md border border-gray-700">
          <div className="flex justify-center pt-3 pb-1 sm:hidden">
            <div className="w-10 h-1 bg-gray-600 rounded-full" />
          </div>
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-2 min-w-0">
              <div className={`p-2 rounded-lg flex-shrink-0 ${cfg.bgColor}`}>
                <Icon className={`w-5 h-5 ${cfg.color}`} />
              </div>
              <h3 className="text-white font-semibold truncate">{event.title}</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1 ml-2 flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-start gap-2 text-gray-400 text-sm">
              <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{new Date(event.start_time).toLocaleString()} – {new Date(event.end_time).toLocaleTimeString()}</span>
            </div>
            {event.description && (
              <div className="pt-2 border-t border-gray-700">
                <p className="text-gray-400 text-sm">{event.description}</p>
              </div>
            )}
            {event.tail_number && (
              <div className="pt-2 border-t border-gray-700">
                <p className="text-xs text-gray-500">Aircraft</p>
                <p className="text-white text-sm">{event.tail_number}</p>
              </div>
            )}
            <div className="pt-2 border-t border-gray-700">
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <StatusBadge status={event.status} />
            </div>
          </div>
          <div className="p-4 border-t border-gray-700 pb-safe">
            <button onClick={onClose}
              className="w-full py-3 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-xl text-white font-medium transition">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-0">
      <div className="flex flex-col gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold text-white">My Calendar</h2>
          <p className="text-gray-400 text-sm">View your scheduled flights and appointments</p>
        </div>
        <div className="flex bg-gray-700 rounded-lg p-0.5">
          {['day', 'week', 'month'].map(mode => (
            <button key={mode} onClick={() => setViewMode(mode)}
              className={`flex-1 py-1.5 text-sm rounded-md transition font-medium ${
                viewMode === mode ? 'bg-blue-500 text-white' : 'text-gray-400'
              }`}>
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1">
          <button onClick={handlePrev} className="p-2 hover:bg-gray-700 active:bg-gray-600 rounded-lg text-gray-400 transition">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => { setCurrentDate(new Date()); setSelectedEvent(null); }}
            className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition">
            Today
          </button>
          <button onClick={handleNext} className="p-2 hover:bg-gray-700 active:bg-gray-600 rounded-lg text-gray-400 transition">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <h3 className="text-sm font-semibold text-white truncate ml-2">{formatHeader()}</h3>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'day' && renderDayView()}
        </>
      )}

      <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
};

export default ClientCalendar;